import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { Card, CardHeader, CardContent } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import SlaBadge from "../components/ui/SlaBadge";
import PersonChip from "../components/ui/PersonChip";
import OriginIcon from "../components/ui/OriginIcon";
import { msPhotoUrl, buildAnalystByName, assignedAvatarSrc } from "../utils/avatars";
import { ticketOrigin, eventOrigin } from "../utils/origin";
import api from "../services/api";
import knowledgeService from "../services/knowledgeService";
import { BookOpen, BookPlus, Send } from "lucide-react";
import EditTicketModal from "../components/tickets/EditTicketModal";
import CloseTicketModal from "../components/tickets/CloseTicketModal";
import ApprovalRequestModal from "../components/tickets/ApprovalRequestModal";
import DOMPurify from "dompurify";
import { parseApiDateTime } from "../utils/datetime";
import ticketCategoriesService from "../services/ticketCategoriesService";

// Corrige "mojibake" (UTF-8 lido como Latin-1/Windows-1252) em descrições e
// comentários vindos de e-mail. Só aplica se reduzir os sinais de encoding quebrado.
// Reverso do Windows-1252 (0x80–0x9F): aspas/cedilha/travessão/etc. viram
// code points > 0xFF quando o e-mail foi lido como cp1252 — mapeia de volta pro byte.
const CP1252_REV = {
  "€": 0x80, "‚": 0x82, "ƒ": 0x83, "„": 0x84, "…": 0x85,
  "†": 0x86, "‡": 0x87, "ˆ": 0x88, "‰": 0x89, "Š": 0x8A,
  "‹": 0x8B, "Œ": 0x8C, "Ž": 0x8E, "‘": 0x91, "’": 0x92,
  "“": 0x93, "”": 0x94, "•": 0x95, "–": 0x96, "—": 0x97,
  "˜": 0x98, "™": 0x99, "š": 0x9A, "›": 0x9B, "œ": 0x9C,
  "ž": 0x9E, "Ÿ": 0x9F,
};

function fixMojibake(s) {
  if (typeof s !== "string" || !s) return s;
  if (!/[ÃÂ]|â€/.test(s)) return s;
  try {
    const bytes = Uint8Array.from(s, (c) =>
      c in CP1252_REV ? CP1252_REV[c] : c.charCodeAt(0) & 0xff
    );
    const decoded = new TextDecoder("utf-8", { fatal: false }).decode(bytes);
    const noise = (t) => (t.match(/Ã|Â|â€|�/g) || []).length;
    return noise(decoded) < noise(s) ? decoded : s;
  } catch {
    return s;
  }
}

function renderHtml(content, emptyMessage = "Nenhuma descrição informada.") {
  if (!content) {
    return <p className="text-gray-500 italic text-sm">{emptyMessage}</p>;
  }

  // Sanitiza o HTML de e-mail antes de injetar (evita XSS via <script>, onerror,
  // javascript:, etc.). DOMPurify preserva imagens inline data:/cid: (DATA_URI_TAGS),
  // entao os anexos embutidos continuam aparecendo.
  const clean = DOMPurify.sanitize(fixMojibake(content));
  const hasHtml = /<\/?[a-z][\s\S]*>/i.test(content);

  // E-mail (HTML com cores próprias) → cartão branco forçado, legível no dark.
  // Texto puro (chamado criado pela web) → respeita o tema (dark-aware).
  if (hasHtml) {
    return (
      <div
        className="email-surface text-sm leading-relaxed break-words"
        dangerouslySetInnerHTML={{ __html: clean }}
      />
    );
  }

  return (
    <div
      className="text-sm text-gray-700 leading-relaxed break-words whitespace-pre-wrap"
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
}

function renderCommentContent(comment) {
  if (!comment) return null;

  const hasHtmlTag = /<\/?[a-z][\s\S]*>/i.test(comment);

  if (hasHtmlTag) {
    return renderHtml(comment, "");
  }

  return <p className="text-gray-700 whitespace-pre-wrap text-sm">{fixMojibake(comment)}</p>;
}

const TicketDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);

  const [newComment, setNewComment] = useState("");
  const [replyAll, setReplyAll] = useState(true); // resposta: Todos (Para/CC) x só remetente
  const [commentLoading, setCommentLoading] = useState(false);

  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [analysts, setAnalysts] = useState([]);
  const analystByName = useMemo(() => buildAnalystByName(analysts), [analysts]);
  const [categories, setCategories] = useState([]);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isCloseOpen, setIsCloseOpen] = useState(false);
  const [isApprovalOpen, setIsApprovalOpen] = useState(false);
  const [suggestedApprover, setSuggestedApprover] = useState({ email: "", name: "" });
  const [kbSuggestions, setKbSuggestions] = useState([]);
  const [sendingArticleId, setSendingArticleId] = useState(null);

  const formatDateTime = (value) => {
    const date = parseApiDateTime(value);
    if (!date) return "-";
    return date.toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" });
  };

  useEffect(() => {
    fetchTicket();
    fetchAnalysts();
    ticketCategoriesService
      .list()
      .then((data) => setCategories(Array.isArray(data) ? data : []))
      .catch(() => setCategories([]));
    knowledgeService.suggest(id).then((d) => setKbSuggestions(Array.isArray(d) ? d : [])).catch(() => setKbSuggestions([]));
  }, [id]);

  const stripHtml = (html) => {
    const tmp = document.createElement("div");
    tmp.innerHTML = html || "";
    return (tmp.textContent || tmp.innerText || "").trim();
  };

  const createArticleFromTicket = () => {
    navigate("/base-conhecimento", {
      state: { prefill: { title: ticket?.title || "", category: ticket?.category || "", body: ticket?.description || "" } },
    });
  };

  const sendArticleToRequester = async (articleId) => {
    setSendingArticleId(articleId);
    try {
      const art = await knowledgeService.get(articleId);
      const text = `Compartilho um artigo da nossa base que pode ajudar:\n\n${art.title}\n\n${stripHtml(art.body)}`;
      await api.post(`/tickets/${id}/comments`, { comment: text });
      await fetchTicket();
      alert("Artigo enviado ao solicitante (registrado como comentário).");
    } catch (e) {
      alert(e.response?.data?.detail || "Erro ao enviar o artigo.");
    } finally {
      setSendingArticleId(null);
    }
  };

  const fetchTicket = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/tickets/${id}`);
      setTicket(response.data);
    } catch (error) {
      console.error("Erro ao buscar chamado:", error.response?.data || error);
      alert("Erro ao carregar o chamado.");
      navigate("/tickets");
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalysts = async () => {
    try {
      // Lista de "fila" (is_assignable), mesmo critério da tela de Chamados.
      const response = await api.get("/users/assignable");
      setAnalysts(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Erro ao buscar responsáveis (fila):", error);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      setCommentLoading(true);
      await api.post(`/tickets/${id}/comments`, {
        comment: newComment.trim(),
        // Responder a Todos = copia quem estava no Para/CC do chamado.
        cc_emails: replyAll ? (ticket.participant_emails || "") : "",
      });
      setNewComment("");
      await fetchTicket();
    } catch (error) {
      console.error("Erro ao adicionar comentário:", error.response?.data || error);
      alert("Erro ao adicionar comentário.");
    } finally {
      setCommentLoading(false);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    } else {
      setFile(null);
    }
  };

  const handleUploadAttachment = async (e) => {
    e.preventDefault();
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      setUploading(true);
      await api.post(`/tickets/${id}/attachments`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFile(null);
      await fetchTicket();
    } catch (error) {
      console.error("Erro ao enviar anexo:", error.response?.data || error);
      alert("Erro ao enviar anexo.");
    } finally {
      setUploading(false);
    }
  };

  const openEdit = () => setIsEditOpen(true);
  const closeEdit = () => setIsEditOpen(false);

  const openApproval = async () => {
    setSuggestedApprover({ email: "", name: "" });
    setIsApprovalOpen(true);
    try {
      const res = await api.get(`/tickets/${id}/suggested-approver`);
      setSuggestedApprover({ email: res.data?.email || "", name: res.data?.name || "" });
    } catch (e) {
      // sem sugestão: o analista preenche manualmente
    }
  };

  const handleApprovalSubmit = async (payload) => {
    try {
      await api.post(`/tickets/${id}/request-approval`, payload);
      setIsApprovalOpen(false);
      await fetchTicket();
    } catch (error) {
      console.error("Erro ao solicitar aprovação:", error.response?.data || error);
      alert(error.response?.data?.detail || "Erro ao solicitar aprovação.");
    }
  };

  const handleCloseSubmit = async ({ comment, cc_emails, file: closeFile }) => {
    const fd = new FormData();
    if (comment) fd.append("comment", comment);
    if (cc_emails) fd.append("cc_emails", cc_emails);
    if (closeFile) fd.append("file", closeFile);
    try {
      await api.post(`/tickets/${id}/close`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setIsCloseOpen(false);
      await fetchTicket();
    } catch (error) {
      console.error("Erro ao encerrar chamado:", error.response?.data || error);
      alert("Erro ao encerrar o chamado.");
    }
  };

  // Baixa/abre o anexo autenticado (a rota agora exige login + tenant; <a href> não manda token).
  const openAttachment = async (attachmentId, download = false, filename = "") => {
    try {
      const res = await api.get(
        `/tickets/attachments/${attachmentId}${download ? "?download=true" : ""}`,
        { responseType: "blob" }
      );
      const blobUrl = window.URL.createObjectURL(res.data);
      if (download) {
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = filename || "anexo";
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else {
        window.open(blobUrl, "_blank", "noopener,noreferrer");
      }
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
    } catch (error) {
      console.error("Erro ao abrir anexo:", error.response?.data || error);
      alert("Erro ao abrir o anexo.");
    }
  };

  const handleEditSubmit = async (formData) => {
    const ticketPayload = {
      status: formData.status,
      priority: formData.priority,
      category: formData.category,
      assigned_to: formData.assigned_to,
    };

    try {
      await api.put(`/tickets/${id}`, ticketPayload);

      if (formData.comment && formData.comment.trim() !== "") {
        await api.post(`/tickets/${id}/comments`, {
          comment: formData.comment.trim(),
        });
      }

      await fetchTicket();
      closeEdit();
    } catch (error) {
      console.error("Erro ao atualizar chamado:", error.response?.data || error);
      alert("Erro ao atualizar chamado.");
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64 text-gray-500">
          Carregando chamado...
        </div>
      </Layout>
    );
  }

  if (!ticket) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64 text-gray-500">
          Chamado não encontrado.
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Chamado #{ticket.id}</h1>
            <p className="text-gray-500">{ticket.title}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate("/tickets")}>
              Voltar para lista
            </Button>
            <Button variant="outline" onClick={openEdit}>Editar</Button>
            {ticket.status !== "Encerrado" && ticket.status !== "Cancelado" && ticket.status !== "Aguardando aprovação" && (
              <Button variant="outline" onClick={openApproval}>Solicitar aprovação</Button>
            )}
            {ticket.status !== "Encerrado" && ticket.status !== "Cancelado" && (
              <Button onClick={() => setIsCloseOpen(true)}>Encerrar chamado</Button>
            )}
          </div>
        </div>

        {ticket.approval && (
          <div className={`rounded-lg border px-4 py-3 text-sm ${
            ticket.approval.status === "pendente"
              ? "border-cyan-200 bg-cyan-50 text-cyan-800"
              : ticket.approval.status === "aprovado"
              ? "border-emerald-200 bg-emerald-50 text-emerald-800"
              : "border-red-200 bg-red-50 text-red-800"
          }`}>
            {ticket.approval.status === "pendente" ? (
              <span><strong>Aguardando aprovação</strong> de {ticket.approval.approver_name || ticket.approval.approver_email}.</span>
            ) : ticket.approval.status === "aprovado" ? (
              <span><strong>Aprovado</strong> por {ticket.approval.approver_name || ticket.approval.approver_email}.</span>
            ) : (
              <span><strong>Reprovado</strong> por {ticket.approval.approver_name || ticket.approval.approver_email}.</span>
            )}
            {ticket.approval.decision_note && (
              <span className="block mt-1 opacity-90">Observação: {ticket.approval.decision_note}</span>
            )}
          </div>
        )}

        <Card>
          <CardContent className="flex flex-wrap items-center gap-6 text-sm py-4">
            <p className="flex items-center gap-2"><span className="font-semibold">Status:</span> {ticket.status} <SlaBadge status={ticket.sla_status} /></p>
            <p><span className="font-semibold">Prioridade:</span> {ticket.priority}</p>
            <p className="flex items-center gap-2"><span className="font-semibold">Responsável:</span>
              <PersonChip name={ticket.assigned_to} src={assignedAvatarSrc(ticket.assigned_to, analystByName)} /></p>
            <p className="flex items-center gap-2"><span className="font-semibold">Solicitante:</span>
              <PersonChip name={ticket.requester_name} src={msPhotoUrl(ticket.requester_email, ticket.requester_name)} /></p>
            <p><span className="font-semibold">Criado em:</span> {formatDateTime(ticket.created_at)}</p>
            <p className="flex items-center gap-2">
              <span className="font-semibold">Origem:</span>
              <OriginIcon origin={ticketOrigin(ticket)} size={22} />
              {ticketOrigin(ticket) === "auto" ? "Automático" : "Manual"}
            </p>
            {ticket.participant_emails && (
              <p className="w-full flex flex-wrap items-baseline gap-x-2 gap-y-1 border-t border-gray-100 pt-3">
                <span className="font-semibold">Também no chamado (Para/CC):</span>
                <span className="text-gray-600 break-words">{ticket.participant_emails}</span>
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold">Descrição</h2>
          </CardHeader>
          <CardContent>{renderHtml(ticket.description)}</CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between gap-3 mb-3">
              <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                <BookOpen size={18} className="text-blue-600" /> Artigos relacionados
              </h2>
              <Button variant="outline" onClick={createArticleFromTicket} className="h-auto gap-2 px-3 py-1.5 text-gray-700">
                <BookPlus size={16} /> Criar artigo deste chamado
              </Button>
            </div>
            {kbSuggestions.length === 0 ? (
              <p className="text-sm text-gray-500">Nenhum artigo da base bate com este chamado.</p>
            ) : (
              <div className="divide-y divide-gray-100">
                {kbSuggestions.map((s) => (
                  <div key={s.id} className="flex items-start justify-between gap-3 py-3">
                    <button type="button" onClick={() => navigate(`/base-conhecimento/${s.id}`)} className="min-w-0 text-left">
                      <div className="font-medium text-gray-900">{s.title}</div>
                      {s.snippet && <div className="text-sm text-gray-500 line-clamp-2">{s.snippet}</div>}
                    </button>
                    <Button variant="outline" disabled={sendingArticleId === s.id}
                      onClick={() => sendArticleToRequester(s.id)}
                      className="h-auto shrink-0 gap-1.5 px-3 py-1.5 text-gray-700">
                      <Send size={14} /> {sendingArticleId === s.id ? "Enviando..." : "Enviar ao solicitante"}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold">Comentários</h2>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              {ticket.comments && ticket.comments.length > 0 ? (
                ticket.comments.map((c) => (
                  <div key={c.id} className="rounded-lg border border-gray-200 px-3 py-2 text-sm">
                    <div className="flex justify-between mb-1">
                      <span className="flex items-center gap-2 font-semibold text-gray-800">
                        <OriginIcon origin={eventOrigin(c.source, c.commenter)} size={20} />
                        {c.commenter}
                      </span>
                      <span className="text-xs text-gray-400">{formatDateTime(c.commented_at)}</span>
                    </div>
                    {renderCommentContent(c.comment)}
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500">Nenhum comentário registrado.</p>
              )}
            </div>

            <form onSubmit={handleAddComment} className="space-y-2">
              <textarea
                className="w-full border rounded-lg px-3 py-2 text-sm"
                rows={3}
                placeholder="Adicionar comentário..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
              />
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                {ticket.participant_emails ? (
                  <label className="flex items-center gap-2 text-xs text-gray-600">
                    <input type="checkbox" checked={replyAll} onChange={(e) => setReplyAll(e.target.checked)} />
                    Responder a <strong>todos</strong> (solicitante + Para/CC)
                  </label>
                ) : <span />}
                <Button type="submit" disabled={commentLoading}>
                  {commentLoading ? "Salvando..." : "Adicionar comentário"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold">Histórico de Alterações</h2>
          </CardHeader>
          <CardContent>
            {ticket.history && ticket.history.length > 0 ? (
              <ul className="space-y-2 text-sm">
                {ticket.history
                  .slice()
                  .sort((a, b) => new Date(a.changed_at) - new Date(b.changed_at))
                  .map((h) => (
                    <li key={h.id} className="border border-gray-200 rounded-lg px-3 py-2">
                      <div className="flex justify-between mb-1">
                        <span className="flex items-center gap-2 font-semibold text-gray-800">
                          <OriginIcon origin={eventOrigin(h.source, h.changed_by)} size={20} />
                          {h.changed_by}
                        </span>
                        <span className="text-xs text-gray-400">{formatDateTime(h.changed_at)}</span>
                      </div>
                      <p className="text-gray-700 whitespace-pre-wrap">{h.change_description}</p>
                    </li>
                  ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500">Nenhuma alteração registrada.</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold">Anexos</h2>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              {ticket.attachments && ticket.attachments.length > 0 ? (
                ticket.attachments.map((a) => (
                  <div
                    key={a.id}
                    className="flex flex-col md:flex-row md:items-center md:justify-between border border-gray-200 rounded-lg px-3 py-2 text-sm gap-2"
                  >
                    <div className="flex flex-col">
                      <span className="text-gray-800">{a.filename}</span>
                      <span className="text-xs text-gray-400">{formatDateTime(a.uploaded_at)}</span>
                    </div>

                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => openAttachment(a.id, false)}>
                        Visualizar
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => openAttachment(a.id, true, a.filename)}>
                        Download
                      </Button>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500">Nenhum arquivo anexado.</p>
              )}
            </div>

            <form onSubmit={handleUploadAttachment} className="flex flex-col md:flex-row gap-2 md:items-center">
              <input type="file" onChange={handleFileChange} className="text-sm" />
              <Button type="submit" disabled={uploading || !file}>
                {uploading ? "Enviando..." : "Anexar arquivo"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      <EditTicketModal
        isOpen={isEditOpen}
        onClose={closeEdit}
        ticket={ticket}
        onSubmit={handleEditSubmit}
        analysts={analysts}
        categories={categories}
      />

      <CloseTicketModal
        isOpen={isCloseOpen}
        onClose={() => setIsCloseOpen(false)}
        ticket={ticket}
        onSubmit={handleCloseSubmit}
      />

      <ApprovalRequestModal
        isOpen={isApprovalOpen}
        onClose={() => setIsApprovalOpen(false)}
        ticket={ticket}
        defaultApprover={suggestedApprover}
        onSubmit={handleApprovalSubmit}
      />
    </Layout>
  );
};

export default TicketDetails;
